import { Box, Flex, HStack, VStack } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { Switch } from '@televet/kibble-ui/build/components/Switch';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import partial from 'lodash-es/partial';
import { ChannelSearchInputKeys } from 'pages/Conversations/interfaces/ChannelSearchInput';
import React, { useMemo } from 'react';

interface ChannelSearchListProps {
  onClose: () => void;
  searchTextPetParentNames: string;
  searchTextPetNames: string;
  searchTextMessageBodies: string;
  showArchived: boolean;
  showSearchResultsCount?: boolean;
  searchResultsCount?: number;
  isFilterApplied: boolean;
  searchTextClientNameInputRefCallback: (node: HTMLInputElement | null) => void;
  onSearchInputChange: (key: ChannelSearchInputKeys, event: React.ChangeEvent<HTMLInputElement>) => void;
  onShowArchivedToggle: () => void;
  onSearchInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onRemoveFilters: (source?: string | undefined) => void;
}

export const ChannelSearchList = ({
  searchTextMessageBodies,
  searchTextPetNames,
  searchTextPetParentNames,
  showArchived,
  showSearchResultsCount = false,
  searchResultsCount,
  isFilterApplied,
  onClose,
  searchTextClientNameInputRefCallback,
  onSearchInputChange,
  onShowArchivedToggle,
  onSearchInputKeyDown,
  onRemoveFilters,
}: ChannelSearchListProps): JSX.Element => {
  const conversationType = useMemo(() => {
    let conversationSet = 'all';
    if (isFilterApplied) {
      conversationSet = 'filtered';
    } else if (!showArchived) {
      conversationSet = 'active';
    }
    return conversationSet;
  }, [isFilterApplied, showArchived]);

  return (
    <Flex className="ChannelListToolbar__ChannelSearchContainer" direction="column" w="100%" p="3">
      <Flex className="ChannelListToolbar__ChannelSearchHeader" align="center" justify="space-between" pb="1">
        <Heading size="sm">Search {conversationType} conversations</Heading>
        <Button size="sm" variant="ghostNeutral" iconName="close" onClick={onClose} />
      </Flex>
      <VStack spacing="3" position="relative" w="100%" align="stretch">
        <TextInput
          name="searchTextClientName"
          ref={searchTextClientNameInputRefCallback}
          type="text"
          placeholder="Search by client name"
          value={searchTextPetParentNames}
          onChange={partial(onSearchInputChange, ChannelSearchInputKeys.PetParentNames)}
          onKeyDown={onSearchInputKeyDown}
          onFocus={(e): void => e.target.select()}
          data-mixpanel-name="Conversation search client name input"
          data-testid="conversation-search-client-name-input"
        />
        <TextInput
          name="searchTextPatientName"
          type="text"
          placeholder="Search by patient name"
          value={searchTextPetNames}
          onChange={partial(onSearchInputChange, ChannelSearchInputKeys.PetNames)}
          onKeyDown={onSearchInputKeyDown}
          onFocus={(e): void => e.target.select()}
          data-mixpanel-name="Conversation search patient name input"
          data-testid="conversation-search-patient-name-input"
        />
        <TextInput
          name="searchTextMessageBody"
          type="text"
          placeholder="Search by message"
          value={searchTextMessageBodies}
          onChange={partial(onSearchInputChange, ChannelSearchInputKeys.MessageBodies)}
          onKeyDown={onSearchInputKeyDown}
          onFocus={(e): void => e.target.select()}
          data-mixpanel-name="Conversation search message name input"
          data-testid="conversation-search-message-input"
        />
      </VStack>
      <Flex>
        <HStack as="label" cursor="pointer" pt="3">
          <Box>
            <Switch isFieldInline isChecked={showArchived} onChange={onShowArchivedToggle} />
          </Box>
          <Text size="sm">Show Archived</Text>
        </HStack>
      </Flex>
      {showSearchResultsCount && (
        <Flex align="baseline" justify="space-between" fontSize="sm" pt="3">
          <Text size="sm">
            {searchResultsCount === 0 ? 'No' : searchResultsCount} result{searchResultsCount === 1 ? '' : 's'} from{' '}
            {conversationType} conversations
          </Text>
          {isFilterApplied && (
            <Button variant="ghost" onClick={(): void => onRemoveFilters('search-panel-results-count')} size="sm">
              Clear filters
            </Button>
          )}
        </Flex>
      )}
    </Flex>
  );
};
